// The "Sound" tool (issue 1161). Everyone: what the table is playing, and their own mixer — a volume a channel and Mute
// all — kept in this browser and never sent. The DM (and a co-DM with `notes`): the sound library (upload, with the room
// left shown; delete), and the channels — a music playlist, an ambience loop, the effects board — with play and stop.
import { useEffect, useState } from 'react';
import { AUDIO_LIMITS, ZONE_LABELS, type AudioChannel, type AudioInfo, type Cue, type CueChannel, type CueOn, type CueTrigger, type TableObject, type Zone } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useShell } from '../context';
import { selection } from '../../board/selection';
import { deleteSound, listCues, listSounds, loadMixer, onMixer, roomWords, saveMixer, uploadSound, type Mixer, type Room } from '../../sound';
import { BTN } from '../ui';

const btn = `${BTN} shrink-0`;
const CHANNEL_WORDS: Record<AudioChannel, string> = { music: 'Music', ambience: 'Ambience', effect: 'Effects' };

/** This person's mixer: a slider a channel and Mute all. Stored in this browser; nothing is sent. */
function MixerControls() {
  const [mixer, setMixer] = useState<Mixer>(() => loadMixer());
  useEffect(() => onMixer(setMixer), []);
  const change = (next: Mixer) => { setMixer(next); saveMixer(next); };
  return (
    <fieldset className="space-y-1 rounded border border-stone-700 p-2" data-testid="sound-mixer">
      <legend className="text-xs text-stone-400">Your volume (only you hear these)</legend>
      {(['music', 'ambience', 'effect'] as const).map((ch) => (
        <label key={ch} className="flex items-center gap-2 text-xs">
          <span className="w-16">{CHANNEL_WORDS[ch]}</span>
          <input type="range" min={0} max={100} value={Math.round(mixer[ch] * 100)} aria-label={`${CHANNEL_WORDS[ch]} volume`}
            onChange={(e) => change({ ...mixer, [ch]: Number(e.target.value) / 100 })} className="flex-1" />
        </label>
      ))}
      <label className="flex items-center gap-1 text-xs">
        <input type="checkbox" data-testid="sound-mute" checked={mixer.muted} onChange={(e) => change({ ...mixer, muted: e.target.checked })} />Mute all
      </label>
    </fieldset>
  );
}

/** What the table is playing now, in words, for everyone. */
function NowPlaying() {
  const { audio } = useTableState();
  const line = (ch: 'music' | 'ambience') => {
    const p = audio[ch];
    if (!p) return null;
    const t = p.tracks[p.index];
    return <li key={ch} data-testid={`now-${ch}`}>{CHANNEL_WORDS[ch]}: {t?.name}{p.tracks.length > 1 ? ` (${p.index + 1} of ${p.tracks.length})` : ''}{p.loop ? ', looping' : ''}</li>;
  };
  const lines = [line('music'), line('ambience')].filter(Boolean);
  return lines.length ? <ul className="text-xs" data-testid="now-playing">{lines}</ul> : <p className="text-xs text-stone-400" data-testid="now-playing">Nothing is playing.</p>;
}

/** The DM's library and channels. */
function SoundDesk() {
  const { act, audio, setNotice } = useTableState();
  const [tracks, setTracks] = useState<AudioInfo[]>([]);
  const [room, setRoom] = useState<Room | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [playlist, setPlaylist] = useState<string[]>([]);
  const [loopList, setLoopList] = useState(true);
  const reload = async () => { const got = await listSounds(); if (got) { setTracks(got.tracks); setRoom(got.room); } };
  useEffect(() => { void reload(); }, []);

  const upload = async (file: File) => {
    setBusy(true); setMessage('');
    const name = file.name.replace(/\.[^.]+$/, '').trim().slice(0, AUDIO_LIMITS.name) || 'A sound';
    const r = await uploadSound(name, file);
    setBusy(false);
    if (r.kind === 'ok') { setRoom(r.room); setTracks((cur) => [r.track, ...cur]); setMessage(`${r.track.name} is in your sounds.`); }
    else setMessage(r.kind === 'refused' ? r.reason : 'That upload did not go through. Try again.');
  };
  const remove = async (t: AudioInfo) => {
    const r = await deleteSound(t.id);
    if (r) { setRoom(r); setTracks((cur) => cur.filter((x) => x.id !== t.id)); setPlaylist((cur) => cur.filter((x) => x !== t.id)); }
    else setMessage('That sound could not be deleted.');
  };
  const play = (channel: AudioChannel, ids: string[], loop: boolean) => act({ kind: 'audio.play', channel, tracks: ids, loop }, (r) => { if (r.ok && channel !== 'effect') setNotice(`${CHANNEL_WORDS[channel]} playing for the table.`); });
  const stop = (channel: AudioChannel) => act({ kind: 'audio.stop', channel });
  const left = room ? Math.max(0, room.cap - room.used) : null;

  return (
    <section className="space-y-2" aria-label="Your sounds">
      <div className="space-y-1 rounded border border-stone-700 p-2">
        <label className="block text-xs">Upload a sound (MP3, OGG, WAV or M4A; up to {AUDIO_LIMITS.bytes / (1024 * 1024)} MB)
          <input type="file" accept="audio/*,.mp3,.ogg,.oga,.opus,.wav,.m4a,.aac" data-testid="sound-upload" disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} className="block text-xs" />
        </label>
        {room && <p className="text-xs text-stone-400" data-testid="sound-room">{roomWords(room)} — {(left! / (1024 * 1024)).toFixed(1)} MB left</p>}
        {message && <p className="text-xs text-amber-300" role="status" data-testid="sound-message">{message}</p>}
      </div>
      {tracks.length === 0 ? <p className="text-xs text-stone-400">No sounds yet.</p> : (
        <ul className="space-y-1" data-testid="sound-library">
          {tracks.map((t) => (
            <li key={t.id} className="rounded border border-stone-800 p-1 text-xs" data-testid="sound-track">
              <div className="flex items-center gap-1">
                <input type="checkbox" aria-label={`Add ${t.name} to the playlist`} checked={playlist.includes(t.id)}
                  onChange={(e) => setPlaylist((cur) => (e.target.checked ? [...cur, t.id].slice(0, AUDIO_LIMITS.tracks) : cur.filter((x) => x !== t.id)))} />
                <span className="truncate font-medium">{t.name}</span>
                <span className="ml-auto text-stone-500">{(t.bytes / (1024 * 1024)).toFixed(1)} MB</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                <button type="button" className={btn} onClick={() => play('music', [t.id], false)}>Play as music</button>
                <button type="button" className={btn} onClick={() => play('ambience', [t.id], true)}>Loop as ambience</button>
                <button type="button" className={btn} onClick={() => play('effect', [t.id], false)}>Play as effect</button>
                <button type="button" className={btn} onClick={() => void remove(t)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <button type="button" className={btn} disabled={playlist.length === 0} onClick={() => play('music', playlist, loopList)}>Play playlist ({playlist.length})</button>
        <label className="flex items-center gap-1"><input type="checkbox" checked={loopList} onChange={(e) => setLoopList(e.target.checked)} />Start over at the end</label>
      </div>
      <div className="flex flex-wrap gap-1">
        <button type="button" className={btn} disabled={!audio.music} onClick={() => stop('music')}>Stop music</button>
        <button type="button" className={btn} disabled={!audio.ambience} onClick={() => stop('ambience')}>Stop ambience</button>
        <button type="button" className={btn} disabled={audio.effects.length === 0} onClick={() => stop('effect')}>Stop effects</button>
      </div>
    </section>
  );
}

const TRIGGER_WORDS: Record<Exclude<CueOn, 'prepared'>, string> = {
  area_enter: 'A token enters a zone',
  token_acts: "A token's turn starts",
  attack_hit: 'An attack hits',
  encounter_start: 'The encounter starts',
  encounter_end: 'The encounter ends',
};
const newId = (): string => crypto.randomUUID();

/**
 * The DM's sound cues (issue 1162): a sound tied to a zone, a token's turn, a hit or the fight, played for everyone when
 * it happens. A hidden token never sets one off. The list is the DM's alone, read from the server and re-read after each change.
 */
function CueDesk() {
  const { act, store } = useTableState();
  const { campaignId } = useShell();
  const [cues, setCues] = useState<Cue[]>([]);
  const [tracks, setTracks] = useState<AudioInfo[]>([]);
  const [on, setOn] = useState<CueOn>('area_enter');
  const [zoneId, setZoneId] = useState('');
  const [objectId, setObjectId] = useState('');
  const [trackId, setTrackId] = useState('');
  const [channel, setChannel] = useState<CueChannel>('effect');
  const [message, setMessage] = useState('');
  // A line the cue says (qa-runs#1168): its speaker, its lines (one a line, one said at random), who hears it, how often.
  const [speaker, setSpeaker] = useState('');
  const [sayName, setSayName] = useState('');
  const [lines, setLines] = useState('');
  const [toOwners, setToOwners] = useState(false);
  const [every, setEvery] = useState<'session' | 'always' | 'ever'>('session');
  const reload = async () => { const got = await listCues(campaignId); if (got) setCues(got); };
  useEffect(() => { void reload(); void listSounds().then((got) => { if (got) setTracks(got.tracks); }); }, [campaignId]);
  const zones = ((store.state as { zones?: Zone[] }).zones ?? []);
  const tokens = Object.values(store.state.objects as Record<string, TableObject>).filter((o) => o.kind === 'token');
  const nameOfTrack = (id: string) => tracks.find((t) => t.id === id)?.name ?? 'A sound no longer in your library';
  const nameOfZone = (id: string) => { const z = zones.find((x) => x.id === id); return z ? z.label ?? ZONE_LABELS[z.kind] : 'A zone not on this scene'; };
  const nameOfToken = (id: string) => tokens.find((o) => o.id === id)?.name ?? 'A token not on this scene';
  const describe = (t: CueTrigger): string => {
    if (t.on === 'area_enter') return `A token enters ${nameOfZone(t.zoneId)}`;
    if (t.on === 'token_acts') return `${nameOfToken(t.objectId)}'s turn starts`;
    if (t.on === 'attack_hit') return t.objectId ? `${nameOfToken(t.objectId)} hits` : 'An attack hits';
    if (t.on === 'prepared') return `${nameOfToken(t.objectId)}'s prepared lines`;
    return TRIGGER_WORDS[t.on];
  };
  const needsZone = on === 'area_enter'; const needsToken = on === 'token_acts';
  const trigger = (): CueTrigger | null => {
    if (on === 'area_enter') return zoneId ? { on, zoneId } : null;
    if (on === 'token_acts') return objectId ? { on, objectId } : null;
    if (on === 'attack_hit') return objectId ? { on, objectId } : { on };
    if (on === 'encounter_start' || on === 'encounter_end') return { on };
    return null;
  };
  const t = trigger();
  const said = lines.split('\n').map((l) => l.trim()).filter(Boolean);
  const say = said.length ? { lines: said, ...(speaker ? { speaker } : {}), ...(sayName.trim() ? { name: sayName.trim() } : {}), ...(toOwners ? { to: 'owners' as const } : {}), ...(every !== 'session' ? { every } : {}) } : null;
  const add = () => {
    if (!t || (!trackId && !say)) return;
    setMessage('');
    act({ kind: 'cue.set', cue: { id: newId(), trigger: t, ...(trackId ? { trackId } : {}), channel, ...(say ? { say } : {}) } }, (r) => { if (r.ok) { setLines(''); void reload(); } else setMessage(r.reason ?? 'That cue could not be set.'); });
  };
  const shown = cues.filter((c) => c.trigger.on !== 'prepared');
  const lineWords = (c: Cue): string => {
    if (!c.say) return '';
    const who = c.say.speaker ? nameOfToken(c.say.speaker) : c.say.name ?? 'A voice';
    return `${who} says ${c.say.lines.length === 1 ? `"${c.say.lines[0]}"` : `one of ${c.say.lines.length} lines`}${c.say.to === 'owners' ? ' to whoever set it off' : ''}, ${c.say.every === 'always' ? 'every time' : c.say.every === 'ever' ? 'once ever' : 'once a session'}`;
  };
  const remove = (id: string) => act({ kind: 'cue.remove', id }, () => { void reload(); });
  const fromSelection = () => { const picked = [...selection.get()].find((id) => tokens.some((o) => o.id === id)); if (picked) setObjectId(picked); };
  return (
    <section className="space-y-2" aria-label="Sound cues" data-testid="cue-desk">
      <p className="text-xs text-stone-400">A cue plays one of your sounds, says a line, or both, when something happens. A token no player can see never sets one off.</p>
      <div className="space-y-1 rounded border border-stone-700 p-2 text-xs">
        <label className="block">When
          <select className="ml-1 rounded bg-stone-800" data-testid="cue-trigger" value={on} onChange={(e) => { setOn(e.target.value as CueOn); setObjectId(''); }}>
            {(Object.keys(TRIGGER_WORDS) as Array<keyof typeof TRIGGER_WORDS>).map((k) => <option key={k} value={k}>{TRIGGER_WORDS[k]}</option>)}
          </select>
        </label>
        {needsZone && (zones.length ? (
          <label className="block">Zone
            <select className="ml-1 rounded bg-stone-800" data-testid="cue-zone" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
              <option value="">Choose a zone</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.label ?? ZONE_LABELS[z.kind]}</option>)}
            </select>
          </label>
        ) : <p className="text-stone-400">No zones on this scene. Paint one with View › Paint zones (a Sound area does nothing else).</p>)}
        {(needsToken || on === 'attack_hit') && (
          <div className="flex items-center gap-1">
            <label>Token
              <select className="ml-1 rounded bg-stone-800" data-testid="cue-token" value={objectId} onChange={(e) => setObjectId(e.target.value)}>
                <option value="">{needsToken ? 'Choose a token' : 'Any attacker'}</option>
                {tokens.map((o) => <option key={o.id} value={o.id}>{o.name}{o.hidden ? ' (hidden)' : ''}</option>)}
              </select>
            </label>
            <button type="button" className={btn} onClick={fromSelection}>Use the selected token</button>
          </div>
        )}
        <label className="block">Play
          <select className="ml-1 rounded bg-stone-800" data-testid="cue-track" value={trackId} onChange={(e) => setTrackId(e.target.value)}>
            <option value="">No sound</option>
            {tracks.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <label className="block">As
          <select className="ml-1 rounded bg-stone-800" data-testid="cue-channel" value={channel} onChange={(e) => setChannel(e.target.value as CueChannel)}>
            <option value="effect">an effect (once)</option>
            <option value="music">the music (looping)</option>
          </select>
        </label>
        <fieldset className="space-y-1 rounded border border-stone-800 p-1" data-testid="cue-say">
          <legend className="px-1">Say a line (optional)</legend>
          <label className="block">Speaker
            <select className="ml-1 rounded bg-stone-800" data-testid="cue-speaker" value={speaker} onChange={(e) => setSpeaker(e.target.value)}>
              <option value="">No token (a voice)</option>
              {tokens.map((o) => <option key={o.id} value={o.id}>{o.name}{o.hidden ? ' (hidden)' : ''}</option>)}
            </select>
          </label>
          <label className="block">Name shown (optional)
            <input className="ml-1 rounded bg-stone-800 px-1" data-testid="cue-say-name" maxLength={60} value={sayName} onChange={(e) => setSayName(e.target.value)} />
          </label>
          <label className="block">Lines, one a line (one is said at random)
            <textarea className="mt-1 w-full rounded bg-stone-800 px-1" data-testid="cue-lines" rows={3} value={lines} placeholder={'Welcome, travellers! Mind the forge.'} onChange={(e) => setLines(e.target.value)} />
          </label>
          <label className="block"><input type="checkbox" data-testid="cue-owners" checked={toOwners} onChange={(e) => setToOwners(e.target.checked)} /> Only the players whose token set it off</label>
          <label className="block">How often
            <select className="ml-1 rounded bg-stone-800" data-testid="cue-every" value={every} onChange={(e) => setEvery(e.target.value as 'session' | 'always' | 'ever')}>
              <option value="session">once per session</option>
              <option value="always">every time</option>
              <option value="ever">once ever</option>
            </select>
          </label>
        </fieldset>
        <button type="button" className={btn} data-testid="cue-add" disabled={!t || (!trackId && !say) || said.length > 20 || said.some((l) => l.length > 500)} onClick={add}>Add cue</button>
        {message && <p className="text-amber-300" role="status">{message}</p>}
      </div>
      {shown.length === 0 ? <p className="text-xs text-stone-400" data-testid="cue-none">No cues yet.</p> : (
        <ul className="space-y-1" data-testid="cue-list">
          {shown.map((c) => (
            <li key={c.id} className="flex items-center gap-1 rounded border border-stone-800 p-1 text-xs" data-testid="cue">
              <span className="truncate">{describe(c.trigger)}: {[c.trackId ? `${nameOfTrack(c.trackId)} ${c.channel === 'music' ? 'as music' : 'as an effect'}` : '', lineWords(c)].filter(Boolean).join('; ')}</span>
              {/* The speaker removed from this scene: the cue says nothing (qa-runs#1168). */}
              {c.say?.speaker && !tokens.some((o) => o.id === c.say!.speaker) && <span className="shrink-0 text-amber-300" data-testid="cue-speaker-gone">Speaker not on this scene</span>}
              <button type="button" className={`${btn} ml-auto`} onClick={() => remove(c.id)}>Remove</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function SoundPanel() {
  const { role } = useTableState();
  const mayAct = useMayAct();
  // `notes`'s (issues 1159, 1160), whichever board a co-DM sees; a cue names a zone or a token, so the Cues tab is
  // offered on the DM's board alone.
  const drives = mayAct('audio.play');
  const cues = role === 'dm' && mayAct('cue.set');
  const [tab, setTab] = useState<'sounds' | 'cues'>('sounds');
  return (
    <div className="space-y-2 p-2 text-sm" data-testid="sound-panel">
      <NowPlaying />
      <MixerControls />
      {drives && (
        <>
          <div role="tablist" className="flex gap-1">
            <button type="button" role="tab" aria-selected={tab === 'sounds'} className={`${btn} ${tab === 'sounds' ? 'bg-stone-700' : ''}`} onClick={() => setTab('sounds')}>Sounds</button>
            {cues && <button type="button" role="tab" aria-selected={tab === 'cues'} className={`${btn} ${tab === 'cues' ? 'bg-stone-700' : ''}`} onClick={() => setTab('cues')}>Cues</button>}
          </div>
          {tab === 'sounds' || !cues ? <SoundDesk /> : <CueDesk />}
        </>
      )}
    </div>
  );
}
